import { ChangeContext, Options } from '@angular-slider/ngx-slider';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { BaseComponent } from '@macs-frontend/core';
import { Subscription } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  takeUntil,
  tap,
} from 'rxjs/operators';
import {
  Filter,
  FilterBase,
  IFilter,
  RangeFilter,
  RangeFilterChangeData,
  SearchFilterOptionsData,
  ToggleFilterOptionData,
} from '../../../human-resources/models/filter';

@Component({
  selector: 'md-filter-dropdown',
  templateUrl: './filter-dropdown.component.html',
  styles: [
    `
      :host ::ng-deep .ngx-slider .ngx-slider-pointer::after {
        width: 0;
        height: 0;
      }

      :host ::ng-deep .ngx-slider .ngx-slider-pointer {
        width: 20px;
        height: 20px;
        top: -8px;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterDropdownComponent extends BaseComponent {
  @Output() removeFilter: EventEmitter<FilterBase> = new EventEmitter();
  @Output() resetFilter: EventEmitter<FilterBase> = new EventEmitter();
  @Output()
  toggleFilterOption: EventEmitter<ToggleFilterOptionData> = new EventEmitter();
  @Output()
  searchFilterOptions: EventEmitter<SearchFilterOptionsData> = new EventEmitter();
  @Output()
  rangeFilterChange: EventEmitter<RangeFilterChangeData> = new EventEmitter();

  @Input()
  set filter(_filter: FilterBase) {
    if (_filter.isRangeFilter) {
      this.setupRangeSlider(_filter as RangeFilter);
    }
    this._filter = _filter;
  }

  filterOptionsVisible = false;
  // @ts-expect-error
  rangeSliderOptions: Options;
  // @ts-expect-error
  rangeSliderFormGroup: UntypedFormGroup;
  // @ts-expect-error
  private _filter: FilterBase;
  // @ts-expect-error
  private _rangeSliderChangeSub: Subscription;

  onRemoveFilter(_filter: FilterBase) {
    this.removeFilter.emit(_filter);
  }

  onResetFilter(_filter: FilterBase) {
    this.resetFilter.emit(_filter);
  }

  onSearchFilterOptions(filterOptionsSearchQuery: string, filter: FilterBase) {
    this.searchFilterOptions.emit({
      filterOptionsSearchQuery,
      filter: filter as Filter,
    });
  }

  hideFilterOptions() {
    this.filterOptionsVisible = false;
  }

  onToggleFilterOption(filterName: string, filterOptionName: string, evt: Event) {
    evt.stopPropagation();
    this.toggleFilterOption.emit({ filterName, filterOptionName });
  }

  onRangeSliderChange(changeContext: ChangeContext) {
    const { value, highValue } = changeContext;
    this.rangeSliderFormGroup.setValue({
      from: value,
      to: highValue,
    });
  }

  toggleFilterOptionsVisibility() {
    this.filterOptionsVisible = !this.filterOptionsVisible;
  }

  getOptions(filter: FilterBase) {
    return (filter as unknown as IFilter).options;
  }

  getRange(filter: FilterBase) {
    return (filter as unknown as RangeFilter).range;
  }

  // eslint-disable-next-line @typescript-eslint/adjacent-overload-signatures
  get filter(): FilterBase {
    return this._filter;
  }

  private setupRangeSlider(rangeFilter: RangeFilter) {
    this.rangeSliderOptions = {
      floor: rangeFilter.range.from,
      ceil: rangeFilter.range.to,
      // TODO: make step configurable through adding a parameter to RangeFilter
      step: 1,
      hideLimitLabels: true,
      hidePointerLabels: true,
      noSwitching: true,
    };
    this.rangeSliderFormGroup = new UntypedFormGroup({
      from: new UntypedFormControl(rangeFilter.value.from),
      to: new UntypedFormControl(rangeFilter.value.to),
    });
    if (this._rangeSliderChangeSub) {
      this._rangeSliderChangeSub.unsubscribe();
    }

    const rangeIsValid = (range: { from: number; to: number }): boolean =>
      range.to >= range.from;
    // tried to use pairwise() with this.rangeSliderFormGroup.valueChanges but it doesn.t work
    // that's why prevRange
    // eslint-disable-next-line functional/no-let
    let prevRange = {
      from: rangeFilter.value.from,
      to: rangeFilter.value.to,
    };
    this.rangeSliderFormGroup.valueChanges
      .pipe(
        tap(range => {
          if (rangeIsValid(range)) {
            prevRange = { ...range };
          } else if (rangeIsValid(prevRange)) {
            this.rangeSliderFormGroup.setValue({ ...prevRange });
          }
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();

    this._rangeSliderChangeSub = this.rangeSliderFormGroup.valueChanges
      .pipe(
        filter(rangeIsValid),
        debounceTime(300),
        // TODO: research why distinctUntilChanged is not working
        distinctUntilChanged(
          (previousValue, currentValue) =>
            previousValue.from === currentValue.from &&
            previousValue.to === currentValue.to,
        ),
        tap(value => {
          this.rangeFilterChange.emit({
            from: value.from,
            to: value.to,
            filter: this.filter as RangeFilter,
          });
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }
}
